import React from 'react';

/**
 * ErrorBoundary component to catch and handle React errors
 * Provides a user-friendly error UI instead of crashing the app
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null
    };
  }

  /**
   * Update state when an error is caught
   * @param {Error} error - The error that was thrown
   * @returns {Object} New state with error information
   */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /**
   * Log error information when an error is caught
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Additional error information
   */
  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // You can also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  /**
   * Handle error reset
   */
  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null
    });
  };

  /**
   * Handle page reload
   */
  handleReload = () => {
    window.location.reload();
  };

  /**
   * Render error UI or children
   * @returns {JSX.Element} Error UI or children
   */
  render() {
    if (this.state.hasError) {
      const errorStyles = {
        container: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        card: {
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '600px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        },
        title: {
          color: '#f5576c',
          fontSize: '2rem',
          marginBottom: '20px',
          fontWeight: 'bold'
        },
        message: {
          color: '#666',
          marginBottom: '30px',
          fontSize: '1.1rem',
          lineHeight: '1.6'
        },
        button: {
          padding: '12px 24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '10px',
          fontSize: '1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          margin: '0 10px',
          transition: 'transform 0.2s, box-shadow 0.2s'
        },
        buttonHover: {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
        },
        buttonGroup: {
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }
      };

      return (
        <div style={errorStyles.container}>
          <div style={errorStyles.card}>
            <h1 style={errorStyles.title}>
              ⚠️ Something went wrong
            </h1>
            <p style={errorStyles.message}>
              {this.state.error?.message || 'An unexpected error occurred. Please try reloading the page.'}
            </p>
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details style={{ 
                marginBottom: '20px', 
                textAlign: 'left',
                background: '#f5f5f5',
                padding: '15px',
                borderRadius: '8px',
                fontSize: '0.9rem'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{ 
                  overflow: 'auto', 
                  maxHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {this.state.error?.stack || this.state.error?.toString()}
                </pre>
              </details>
            )}
            <div style={errorStyles.buttonGroup}>
              <button
                onClick={this.handleReset}
                style={errorStyles.button}
                onMouseEnter={(e) => Object.assign(e.target.style, errorStyles.buttonHover)}
                onMouseLeave={(e) => {
                  e.target.style.transform = '';
                  e.target.style.boxShadow = '';
                }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                style={errorStyles.button}
                onMouseEnter={(e) => Object.assign(e.target.style, errorStyles.buttonHover)}
                onMouseLeave={(e) => {
                  e.target.style.transform = '';
                  e.target.style.boxShadow = '';
                }}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

